import React from 'react';
import { PendingPayableRow } from 'services/reportsApi';

interface Props {
  rows: PendingPayableRow[];
  onInvite?: (row: PendingPayableRow) => void;
  invitingKey?: string | null;
  invitedKeys?: Set<string>;
  // Managed onboarding (super-admin): directly onboard the vendor instead of
  // inviting them to self-register. Only rendered when canOnboard is true.
  onOnboard?: (row: PendingPayableRow) => void;
  canOnboard?: boolean;
}

const fmtMoney = (n: number): string => {
  if (!Number.isFinite(n)) return '—';
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Date-only value (YYYY-MM-DD), e.g. due date.
const fmtDate = (raw: string | null): string => {
  if (!raw) return '—';
  const d = new Date(`${raw}T00:00:00`);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' });
};

// Full ISO timestamp (e.g. first_seen_at) rendered as a date.
const fmtIso = (raw: string | null): string => {
  if (!raw) return '—';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' });
};

export const PendingPayablesTable: React.FC<Props> = ({
  rows,
  onInvite,
  invitingKey,
  invitedKeys,
  onOnboard,
  canOnboard,
}) => {
  const showOnboard = !!canOnboard && !!onOnboard;
  const showActions = !!onInvite || showOnboard;

  const renderInvite = (row: PendingPayableRow) => {
    if (!onInvite) return null;
    const alreadyInvited = row.identityKey ? invitedKeys?.has(row.identityKey) : false;
    if (alreadyInvited) {
      return <span className="text-13s text-green-600">Invited</span>;
    }
    if (!row.canInvite || !row.identityKey) {
      return <span className="text-13s text-gray-400">No contact</span>;
    }
    const isInviting = invitingKey === row.identityKey;
    return (
      <button
        type="button"
        disabled={isInviting}
        onClick={() => onInvite(row)}
        className="rounded border border-indigo-600 px-3 py-1 text-13s font-medium text-indigo-600 hover:bg-indigo-50 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {isInviting ? 'Inviting…' : 'Invite'}
      </button>
    );
  };

  // Managed onboarding needs the vendor's Coupa supplier to link against; rows
  // without one can't be onboarded (mirrors the "No contact" invite guard).
  const renderOnboard = (row: PendingPayableRow) => {
    if (!showOnboard) return null;
    if (!row.coupaSupplierId) {
      return (
        <span className="text-13s text-gray-400" title="No linkable vendor record">
          —
        </span>
      );
    }
    return (
      <button
        type="button"
        onClick={() => onOnboard?.(row)}
        className="rounded bg-indigo-600 px-3 py-1 text-13s font-medium text-white hover:bg-indigo-700"
      >
        Onboard
      </button>
    );
  };

  return (
    <div className="w-full overflow-x-auto">
      <table className="w-full border-separate border-spacing-0 text-14s">
        <thead>
          <tr className="bg-gray-100 text-left text-13s uppercase tracking-wide text-gray-600">
            <th className="px-4 py-2 font-medium">Property</th>
            <th className="px-4 py-2 font-medium">Vendor</th>
            <th className="px-4 py-2 text-right font-medium">Amount</th>
            <th className="px-4 py-2 font-medium">Invoice #</th>
            <th className="px-4 py-2 font-medium">Due date</th>
            <th className="px-4 py-2 font-medium">Pending since</th>
            <th className="px-4 py-2 text-right font-medium">Days pending</th>
            <th className="px-4 py-2 font-medium">Status</th>
            {showActions && <th className="px-4 py-2 text-right font-medium">Action</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id} className="border-b border-gray-200 bg-white hover:bg-gray-50">
              <td className="px-4 py-3 text-gray-900">{row.propertyName ?? 'Unknown property'}</td>
              <td className="px-4 py-3 text-gray-900">
                {row.vendorName ?? row.vendorCode ?? 'Unknown vendor'}
              </td>
              <td className="px-4 py-3 text-right font-medium text-gray-900">
                {fmtMoney(row.amount)}
              </td>
              <td className="px-4 py-3 text-gray-700">{row.invoiceNo ?? '—'}</td>
              <td className="px-4 py-3 text-gray-700">{fmtDate(row.dueDate)}</td>
              <td className="px-4 py-3 text-gray-700">{fmtIso(row.firstSeenAt)}</td>
              <td className="px-4 py-3 text-right text-gray-700">{row.daysPending}</td>
              <td className="px-4 py-3">
                {row.isHeld ? (
                  <span
                    className="whitespace-nowrap rounded-full bg-green-100 px-2 py-0.5 text-13s font-medium text-green-700"
                    title="Funds collected into a hold — awaiting the vendor to register to be paid"
                  >
                    Funds held{row.daysHeld != null ? ` · ${row.daysHeld}d` : ''}
                  </span>
                ) : (
                  <span className="whitespace-nowrap rounded-full bg-gray-100 px-2 py-0.5 text-13s text-gray-600">
                    Awaiting registration
                  </span>
                )}
              </td>
              {showActions && (
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-2">
                    {renderInvite(row)}
                    {renderOnboard(row)}
                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default PendingPayablesTable;
